import { useQuery, useMutation } from '@apollo/client';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as CopyIcon,
  Visibility,
  VisibilityOff,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import {
  Box,
  Container,
  Typography,
  Paper,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
} from '@mui/material';

import { ShareSheet } from '../components/sharing';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type {
  EncryptedVaultItem,
  DecryptedVaultItem,
  TOTPPayload,
  PasswordPayload,
  CreditCardPayload,
  ContactPayload,
  SecureNotePayload,
  VaultItemShare,
} from '../types';
import { decryptItemKey, decryptPayload, decryptItemKeyFromSharing } from '../crypto/encryption';
import { base64ToBytes } from '../crypto/kdf';
import { generateTOTPCode, getTOTPRemainingSeconds } from '../crypto/totp';
import { VAULT_ITEM_QUERY, DELETE_VAULT_ITEM_MUTATION } from '../services/graphql';
import { getCountryName } from '../utils/countries';

export default function VaultItemDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { vaultKey, privateKey } = useAuth();
  const { currentWorkspace } = useWorkspace();

  const [decryptedItem, setDecryptedItem] = useState<DecryptedVaultItem | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showCvv, setShowCvv] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [totpRemaining, setTotpRemaining] = useState(30);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  const workspaceId = currentWorkspace?.id;

  const { data, loading, error } = useQuery(VAULT_ITEM_QUERY, {
    variables: { id, workspaceId },
    skip: !id || !workspaceId,
    fetchPolicy: 'network-only',
  });

  const [deleteItem, { loading: deleteLoading }] = useMutation(DELETE_VAULT_ITEM_MUTATION, {
    onCompleted: () => {
      navigate('/vault');
    },
  });

  // Decrypt item
  useEffect(() => {
    if (!data?.vaultItem || !vaultKey) {
      return;
    }

    const item = data.vaultItem as EncryptedVaultItem;
    let itemKey: Uint8Array | null = null;

    if (item.isOwner) {
      itemKey = decryptItemKey(item.itemKeyEncrypted, item.itemKeyNonce, vaultKey);
    } else {
      if (!privateKey || !item.ownerPublicKey) {
        return;
      }
      const ownerPublicKeyBytes = base64ToBytes(item.ownerPublicKey);
      itemKey = decryptItemKeyFromSharing(
        item.itemKeyEncrypted,
        item.itemKeyNonce,
        ownerPublicKeyBytes,
        privateKey
      );
    }

    if (!itemKey) {
      return;
    }

    const payload = decryptPayload(item.encryptedPayload, item.payloadNonce, itemKey);
    if (!payload) {
      return;
    }

    setDecryptedItem({
      id: item.id,
      type: item.type,
      payload,
      isFavorite: item.isFavorite,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      isOwner: item.isOwner,
      ownerId: item.ownerId,
      ownerEmail: item.ownerEmail,
      ownerPublicKey: item.ownerPublicKey,
      owner: item.owner,
      shares: item.shares || [],
      itemKey,
    });
  }, [data, vaultKey, privateKey]);

  const getOwnerLabel = () => {
    if (!decryptedItem) return '';
    if (decryptedItem.isOwner) {
      return 'Me';
    }
    return decryptedItem.ownerEmail || 'Unknown';
  };

  // TOTP code generation
  useEffect(() => {
    if (!decryptedItem || decryptedItem.payload.type !== 'totp') return;

    const updateTOTP = () => {
      const totpPayload = decryptedItem.payload as TOTPPayload;
      const code = generateTOTPCode(totpPayload);
      setTotpCode(code);
      setTotpRemaining(getTOTPRemainingSeconds(totpPayload.period || 30));
    };

    updateTOTP();
    const interval = setInterval(updateTOTP, 1000);

    return () => clearInterval(interval);
  }, [decryptedItem]);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(label);
    setTimeout(() => setCopySuccess(null), 2000);
  };

  const handleDelete = async () => {
    if (!id) return;
    await deleteItem({ variables: { id } });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">Item not found or access denied</Alert>
      </Container>
    );
  }

  const item = data?.vaultItem as EncryptedVaultItem | undefined;
  if (!decryptedItem) {
    if (item && !item.isOwner && !privateKey) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">
          {item && !item.isOwner && !item.ownerPublicKey
            ? 'Unable to decrypt shared item - owner key not available'
            : 'Item not found or failed to decrypt'}
        </Alert>
      </Container>
    );
  }

  const { payload } = decryptedItem;

  const renderPasswordDetails = () => {
    const pwPayload = payload as PasswordPayload;
    return (
      <>
        {pwPayload.username && (
          <TextField
            fullWidth
            label="Username"
            value={pwPayload.username}
            margin="normal"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleCopy(pwPayload.username!, 'Username')}>
                    <CopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        {pwPayload.password && (
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={pwPayload.password}
            margin="normal"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                  <IconButton onClick={() => handleCopy(pwPayload.password!, 'Password')}>
                    <CopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        {pwPayload.websites && pwPayload.websites.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Websites
            </Typography>
            {pwPayload.websites.map((url, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  value={url}
                  InputProps={{
                    readOnly: true,
                    endAdornment: url && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => window.open(url, '_blank')}
                        >
                          <LaunchIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(url, 'URL')}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1 }}
                />
              </Box>
            ))}
          </>
        )}
      </>
    );
  };

  const renderTOTPDetails = () => {
    return (
      <>
        <Box
          sx={{
            textAlign: 'center',
            py: 3,
            bgcolor: 'primary.light',
            borderRadius: 2,
            mb: 2,
          }}
        >
          <Typography variant="h3" fontWeight={700} color="white" fontFamily="monospace">
            {totpCode.match(/.{1,3}/g)?.join(' ')}
          </Typography>
          <Typography color="white" sx={{ opacity: 0.8, mt: 1 }}>
            Expires in {totpRemaining}s
          </Typography>
        </Box>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<CopyIcon />}
          onClick={() => handleCopy(totpCode, '2FA code')}
        >
          Copy Code
        </Button>
      </>
    );
  };

  const renderSecureNoteDetails = () => {
    const notePayload = payload as SecureNotePayload;
    return (
      <TextField
        fullWidth
        label="Secure Note"
        value={notePayload.secureNote}
        margin="normal"
        multiline
        rows={10}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
              <IconButton onClick={() => handleCopy(notePayload.secureNote, 'Note')}>
                <CopyIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  };

  const renderCreditCardDetails = () => {
    const ccPayload = payload as CreditCardPayload;
    return (
      <>
        {ccPayload.cardholderName && (
          <TextField
            fullWidth
            label="Cardholder Name"
            value={ccPayload.cardholderName}
            margin="normal"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleCopy(ccPayload.cardholderName!, 'Name')}>
                    <CopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        <TextField
          fullWidth
          label="Card Number"
          value={ccPayload.cardNumber}
          margin="normal"
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => handleCopy(ccPayload.cardNumber, 'Card number')}>
                  <CopyIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Grid container spacing={2}>
          {ccPayload.expirationDate && (
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Expiration"
                value={ccPayload.expirationDate}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
            </Grid>
          )}
          {ccPayload.cvv && (
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="CVV"
                type={showCvv ? 'text' : 'password'}
                value={ccPayload.cvv}
                margin="normal"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowCvv(!showCvv)} size="small">
                        {showCvv ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                      <IconButton onClick={() => handleCopy(ccPayload.cvv!, 'CVV')} size="small">
                        <CopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          )}
        </Grid>

        {ccPayload.cardPin && (
          <TextField
            fullWidth
            label="PIN"
            type={showPin ? 'text' : 'password'}
            value={ccPayload.cardPin}
            margin="normal"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPin(!showPin)} size="small">
                    {showPin ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                  <IconButton onClick={() => handleCopy(ccPayload.cardPin!, 'PIN')} size="small">
                    <CopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        {ccPayload.postalCode && (
          <TextField
            fullWidth
            label="Billing Postal Code"
            value={ccPayload.postalCode}
            margin="normal"
            InputProps={{ readOnly: true }}
          />
        )}
      </>
    );
  };

  const renderContactDetails = () => {
    const contactPayload = payload as ContactPayload;
    return (
      <>
        <TextField
          fullWidth
          label="Full Name"
          value={contactPayload.fullName}
          margin="normal"
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => handleCopy(contactPayload.fullName, 'Name')}>
                  <CopyIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {contactPayload.email && (
          <TextField
            fullWidth
            label="Email"
            value={contactPayload.email}
            margin="normal"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleCopy(contactPayload.email!, 'Email')}>
                    <CopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        {contactPayload.phone && (
          <TextField
            fullWidth
            label="Phone"
            value={contactPayload.phone}
            margin="normal"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleCopy(contactPayload.phone!, 'Phone')}>
                    <CopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        {contactPayload.address &&
          Object.values(contactPayload.address).some((v) => v) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Address
              </Typography>
              {contactPayload.address.line1 && (
                <Typography variant="body2">{contactPayload.address.line1}</Typography>
              )}
              {contactPayload.address.line2 && (
                <Typography variant="body2">{contactPayload.address.line2}</Typography>
              )}
              <Typography variant="body2">
                {[
                  contactPayload.address.city,
                  contactPayload.address.stateOrProvince,
                  contactPayload.address.postalCode,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </Typography>
              {contactPayload.address.countryOrRegion && (
                <Typography variant="body2">
                  {getCountryName(contactPayload.address.countryOrRegion)}
                </Typography>
              )}
            </>
          )}
      </>
    );
  };

  return (
    <Box sx={{ flex: 1 }}>
      <Container maxWidth="sm" sx={{ py: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate('/vault')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={600}>
              {payload.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Owner: {getOwnerLabel()}
            </Typography>
          </Box>
          {decryptedItem.isOwner && (
            <>
              <IconButton onClick={() => setShareSheetOpen(true)} color="primary">
                <ShareIcon />
              </IconButton>
              <IconButton onClick={() => navigate(`/edit/${id}`)}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => setDeleteDialogOpen(true)} color="error">
                <DeleteIcon />
              </IconButton>
            </>
          )}
        </Box>

        {copySuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {copySuccess} copied to clipboard
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 2 }}>
          {payload.type === 'password' && renderPasswordDetails()}
          {payload.type === 'totp' && renderTOTPDetails()}
          {payload.type === 'secure_note' && renderSecureNoteDetails()}
          {payload.type === 'credit_card' && renderCreditCardDetails()}
          {payload.type === 'contact' && renderContactDetails()}

          {payload.type !== 'secure_note' && 'notes' in payload && payload.notes && (
            <TextField
              fullWidth
              label="Notes"
              value={payload.notes}
              margin="normal"
              multiline
              rows={3}
              InputProps={{ readOnly: true }}
            />
          )}
        </Paper>

        {/* Shares section */}
        {decryptedItem.isOwner && decryptedItem.shares.length > 0 && (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Sharing
              </Typography>
              <Button
                size="small"
                startIcon={<ShareIcon />}
                onClick={() => setShareSheetOpen(true)}
              >
                Manage
              </Button>
            </Box>
            <List dense>
              {decryptedItem.shares.map((share: VaultItemShare) => (
                <ListItem key={share.id}>
                  <ListItemText
                    primary={share.recipientEmail}
                    secondary={`Shared ${new Date(share.sharedAt).toLocaleDateString()}`}
                  />
                  <Chip
                    size="small"
                    label={share.status}
                    color={share.status === 'accepted' ? 'success' : 'warning'}
                    variant="outlined"
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Container>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Item?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{payload.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" disabled={deleteLoading}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share sheet */}
      {decryptedItem.isOwner && (
        <ShareSheet
          open={shareSheetOpen}
          onClose={() => setShareSheetOpen(false)}
          itemId={decryptedItem.id}
          itemKey={decryptedItem.itemKey}
          shares={decryptedItem.shares}
        />
      )}
    </Box>
  );
}
